import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaDollarSign,
  FaChartBar,
  FaShieldAlt,
  FaRocket,
  FaStar,
} from 'react-icons/fa';
import './Buy.scss';
import Header from '../Header';
import Footer from '../Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import tradingImage from '../../../assets/reddickrednobackground.png';
import {
  ASTROLESCENT_BRIDGE_URL,
  BASE_BUY_URL,
  BASE_CHART_URL,
  BASE_DEXTOOLS_URL,
  BASE_DCKS_CONTRACT_ADDRESS,
  BASE_DCKS_CONTRACT_URL,
  BASE_LOCK_END_LABEL,
  BASE_LOCK_URL,
  BASE_PAIR_LABEL,
  BASE_POOL_CONTRACT_URL,
  BASE_UNISWAP_V3_CONTRACT_ADDRESS,
} from '../../../config/siteContent';

const shortenAddress = (address) =>
  address ? `${address.slice(0, 6)}...${address.slice(-4)}` : '';

const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const observe = useCallback(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) {
      observer.observe(ref.current);
    }
    return observer;
  }, [threshold]);

  useEffect(() => {
    const observer = observe();
    return () => observer?.disconnect();
  }, [observe]);

  return [ref, isVisible];
};

const Buy = () => {
  const baseLockUrl = BASE_LOCK_URL;
  const basePoolContractUrl = BASE_POOL_CONTRACT_URL;
  const baseTokenContractUrl = BASE_DCKS_CONTRACT_URL;
  const baseDextoolsUrl = BASE_DEXTOOLS_URL;
  const c9TradeUrl = 'https://www.caviarnine.com/trade';
  const uniswapUrl = BASE_BUY_URL;
  const chartUrl = BASE_CHART_URL;
  const bridgeUrl = ASTROLESCENT_BRIDGE_URL;

  const [tradingRef, tradingVisible] = useScrollReveal(0.08);
  const [platformsRef, platformsVisible] = useScrollReveal(0.05);
  const [benefitsRef, benefitsVisible] = useScrollReveal(0.08);
  const [roadmapRef, roadmapVisible] = useScrollReveal(0.08);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToPlatforms = (e) => {
    e.preventDefault();
    const platformsSection = document.getElementById('platforms-section');
    if (platformsSection) {
      platformsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="buy-page" data-scroll-root>
      <Header />

      <main className="buy-content">
        <section className="buy-hero">
          <div className="buy-hero__bg-effects">
            <div className="buy-hero__orb buy-hero__orb--1"></div>
            <div className="buy-hero__orb buy-hero__orb--2"></div>
            <div className="buy-hero__orb buy-hero__orb--3"></div>
          </div>
          <div className="container">
            <div className="buy-hero__content">
              <span className="buy-hero__tagline">Liquidity With Public Receipts</span>
              <h1 className="buy-hero__title">
                <span className="buy-hero__title-line">Buy REDDICKS</span>
                <span className="buy-hero__title-brand">on Base</span>
              </h1>
              <p className="buy-hero__subtitle">
                Base-first entrypoint with live, publicly locked liquidity and a
                1% fee model feeding treasury growth. Verify Team Finance,
                DEXTools, and the official Base contracts before you trade.
              </p>
              <div className="buy-hero__actions">
                <a
                  href={uniswapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-button buy-cta buy-cta--primary"
                >
                  Buy on Base <FaArrowRight />
                </a>
                <a href={chartUrl} target="_blank" rel="noopener noreferrer" className="cta-button buy-cta buy-cta--secondary">
                  View Chart
                </a>
                <Link to="/verify" className="cta-button buy-cta buy-cta--secondary">Verify Liquidity</Link>
              </div>
            </div>
          </div>
          <div className="buy-hero__scroll-indicator">
            <span></span>
          </div>
        </section>

        <section ref={tradingRef} className={`buy-trading ${tradingVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <div className="buy-trading__layout">
              <div className="buy-trading__mascot">
                <div className="buy-trading__mascot-glow"></div>
                <img
                  src={tradingImage}
                  alt="REDDICKS Trading"
                  className="buy-trading__mascot-img"
                />
              </div>
              <div className="buy-trading__info">
                <h2 className="section-title">Liquidity Strategy, Plain and Clear</h2>
                <p className="buy-trading__lead">
                  REDDICKS positions liquidity as a trust anchor across Radix and
                  Base, with public lock visibility and straightforward fee
                  design.
                </p>

                <div className="buy-trading__cards">
                  <div className="buy-info-card buy-info-card--crimson">
                    <div className="buy-info-card__accent"></div>
                    <div className="buy-info-card__content">
                      <h3>Lock-First Trust Model</h3>
                      <p>
                        Liquidity is intended to stay publicly verifiable so
                        users can confirm lock status before they buy.
                      </p>
                    </div>
                  </div>

                  <div className="buy-info-card buy-info-card--violet">
                    <div className="buy-info-card__accent"></div>
                    <div className="buy-info-card__content">
                      <h3>Base Is Live and Locked</h3>
                      <p>
                        Base liquidity is live on <strong>{BASE_PAIR_LABEL}</strong>
                        with the published lock visible on <strong>Team Finance</strong>.
                        DEXTools and BaseScan provide the supporting market and
                        contract references.
                      </p>
                    </div>
                  </div>

                  <div className="buy-info-card buy-info-card--cyan">
                    <div className="buy-info-card__accent"></div>
                    <div className="buy-info-card__content">
                      <h3>Treasury-Oriented Fees</h3>
                      <p>
                        Pool fees are treated as project funds for long-term
                        operations and treasury support.
                      </p>
                    </div>
                  </div>

                  <div className="buy-info-card buy-info-card--green">
                    <div className="buy-info-card__accent"></div>
                    <div className="buy-info-card__content">
                      <h3>Bridge Utility</h3>
                      <p>
                        Astrolescent bridge supports route access from Radix to Base with contract references now published.
                      </p>
                      <a href={bridgeUrl} target="_blank" rel="noopener noreferrer" className="buy-inline-link">
                        Open Astrolescent Bridge <FaArrowRight />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="buy-status-grid">
                  <div className="buy-status-card">
                    <span className="buy-status-card__label">Live now</span>
                    <span className="buy-status-card__value">Radix routes active</span>
                  </div>
                  <div className="buy-status-card">
                    <span className="buy-status-card__label">Locked on Base</span>
                    <span className="buy-status-card__value">{BASE_PAIR_LABEL}</span>
                  </div>
                  <div className="buy-status-card">
                    <span className="buy-status-card__label">Verify path</span>
                    <Link to="/verify" className="buy-status-card__link">
                      Open trust portal <FaArrowRight />
                    </Link>
                  </div>
                </div>
                <p className="buy-funnel-note">
                  Base is the on-ramp for discovery. Radix keeps the live utility layer running through DCKBase and staking.
                  <Link to="/dckslap" className="buy-funnel-note__link">Open DCKBase</Link>
                  <Link to="/stake" className="buy-funnel-note__link">Stake DCKS</Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section
          ref={platformsRef}
          className={`buy-platforms ${platformsVisible ? 'visible' : ''}`}
          id="platforms-section"
        >
          <div className="section-divider"></div>
          <div className="container">
            <div className="buy-platforms__header">
              <span className="buy-platforms__tag">Where To Trade</span>
              <h2 className="section-title centered">Trading and Verification Links</h2>
            </div>

            <div className="buy-platform-hero">
              <div className="buy-platform-hero__bg"></div>
              <div className="buy-platform-hero__content">
                <div className="buy-platform-hero__badges">
                  <span className="buy-badge buy-badge--green">Base Live</span>
                  <span className="buy-badge buy-badge--green">{BASE_PAIR_LABEL}</span>
                  <span className="buy-badge buy-badge--green">1% Fee Tier</span>
                </div>
                <h3>Uniswap (Base)</h3>
                <p>
                  Buy route for the live Base pool, then verify the official
                  lock, explorer surface, and contract references from the same proof stack.
                </p>
                <a
                  href={uniswapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-button buy-cta buy-cta--primary"
                >
                  Open Uniswap <FaArrowRight />
                </a>
                <p className="buy-platform-hero__note">
                  Team Finance lock:{' '}
                  <a href={baseLockUrl} target="_blank" rel="noopener noreferrer">
                    live proof
                  </a>
                  {' '}| Pool contract:{' '}
                  <a href={basePoolContractUrl} target="_blank" rel="noopener noreferrer">
                    {shortenAddress(BASE_UNISWAP_V3_CONTRACT_ADDRESS)}
                  </a>
                  {' '}| Token contract:{' '}
                  <a href={baseTokenContractUrl} target="_blank" rel="noopener noreferrer">
                    {shortenAddress(BASE_DCKS_CONTRACT_ADDRESS)}
                  </a>
                </p>
              </div>
            </div>

            <div className="buy-platform-hero" style={{ marginTop: '1rem' }}>
              <div className="buy-platform-hero__content">
                <div className="buy-platform-hero__badges">
                  <span className="buy-badge buy-badge--green">Primary Proof</span>
                  <span className="buy-badge buy-badge--green">DEXTools</span>
                  <span className="buy-badge buy-badge--green">BaseScan</span>
                </div>
                <h3>Base Proof Surfaces</h3>
                <p>
                  Team Finance is the first trust anchor for the Base LP lock.
                  Use DEXTools and BaseScan to confirm the same pair and contract
                  references from public surfaces.
                </p>
                <a
                  href={baseLockUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-button buy-cta buy-cta--primary"
                >
                  Open Team Finance <FaArrowRight />
                </a>
                <p className="buy-platform-hero__note">
                  DEXTools:{' '}
                  <a href={baseDextoolsUrl} target="_blank" rel="noopener noreferrer">
                    pair explorer
                  </a>
                  {' '}| Pool contract:{' '}
                  <a href={basePoolContractUrl} target="_blank" rel="noopener noreferrer">
                    {shortenAddress(BASE_UNISWAP_V3_CONTRACT_ADDRESS)}
                  </a>
                  {' '}| DCKS contract:{' '}
                  <a href={baseTokenContractUrl} target="_blank" rel="noopener noreferrer">
                    {BASE_DCKS_CONTRACT_ADDRESS}
                  </a>
                </p>
                <p className="buy-platform-hero__note">
                  Lock page currently shows the Base LP lock ending on {BASE_LOCK_END_LABEL}.
                </p>
              </div>
            </div>

            <div className="buy-platform-hero" style={{ marginTop: '1rem' }}>
              <div className="buy-platform-hero__content">
                <div className="buy-platform-hero__badges">
                  <span className="buy-badge buy-badge--green">Chart</span>
                  <span className="buy-badge buy-badge--green">Market View</span>
                  <span className="buy-badge buy-badge--green">Live Pair</span>
                </div>
                <h3>Chart and Market Surface</h3>
                <p>
                  Dexscreener stays the chart CTA for Base users, while Team
                  Finance remains the primary lock proof.
                </p>
                <a
                  href={chartUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-button buy-cta buy-cta--primary"
                >
                  Open Dexscreener <FaArrowRight />
                </a>
                <p className="buy-platform-hero__note">
                  DCKS contract:{' '}
                  <a href={baseTokenContractUrl} target="_blank" rel="noopener noreferrer">
                    {BASE_DCKS_CONTRACT_ADDRESS}
                  </a>
                </p>
              </div>
            </div>

            <div className="buy-platforms__strip">
              <a
                href={c9TradeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="buy-platform-strip-card"
              >
                <div className="buy-platform-strip-card__icon-wrap buy-platform-strip-card__icon-wrap--cyan">
                  <FaShieldAlt />
                </div>
                <div className="buy-platform-strip-card__text">
                  <h4>CaviarNine (Radix)</h4>
                  <p>Current Radix venue while Base and bridge routes expand.</p>
                  <div className="buy-platform-strip-card__badges">
                    <span className="buy-badge buy-badge--subtle">DEX</span>
                    <span className="buy-badge buy-badge--subtle">Radix</span>
                  </div>
                </div>
                <FaArrowRight className="buy-platform-strip-card__arrow" />
              </a>

              <a
                href={bridgeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="buy-platform-strip-card"
              >
                <div className="buy-platform-strip-card__icon-wrap buy-platform-strip-card__icon-wrap--coral">
                  <FaStar />
                </div>
                <div className="buy-platform-strip-card__text">
                  <h4>Astrolescent</h4>
                  <p>Aggregator and bridge access from multiple chains.</p>
                  <div className="buy-platform-strip-card__badges">
                    <span className="buy-badge buy-badge--coral">Bridge Route</span>
                    <span className="buy-badge buy-badge--subtle">Aggregator</span>
                    <span className="buy-badge buy-badge--subtle">DEX</span>
                  </div>
                </div>
                <FaArrowRight className="buy-platform-strip-card__arrow" />
              </a>

              <a
                href="https://ociswap.com/resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp"
                target="_blank"
                rel="noopener noreferrer"
                className="buy-platform-strip-card"
              >
                <div className="buy-platform-strip-card__icon-wrap buy-platform-strip-card__icon-wrap--cyan">
                  <FaChartBar />
                </div>
                <div className="buy-platform-strip-card__text">
                  <h4>Ociswap</h4>
                  <p>Radix AMM route used for ecosystem trading access.</p>
                  <div className="buy-platform-strip-card__badges">
                    <span className="buy-badge buy-badge--subtle">DEX</span>
                  </div>
                </div>
                <FaArrowRight className="buy-platform-strip-card__arrow" />
              </a>

              <a
                href="https://www.8arms1goal.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="buy-platform-strip-card"
              >
                <div className="buy-platform-strip-card__icon-wrap buy-platform-strip-card__icon-wrap--violet">
                  <FaRocket />
                </div>
                <div className="buy-platform-strip-card__text">
                  <h4>Calamari</h4>
                  <p>Alternative Radix route with optimizer tooling.</p>
                  <div className="buy-platform-strip-card__badges">
                    <span className="buy-badge buy-badge--subtle">DEX</span>
                  </div>
                </div>
                <FaArrowRight className="buy-platform-strip-card__arrow" />
              </a>
            </div>
          </div>
        </section>

        <section ref={benefitsRef} className={`buy-benefits ${benefitsVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <div className="buy-benefits__header">
              <span className="buy-benefits__tag">Why Trade REDDICKS</span>
              <h2 className="section-title centered">Trading Benefits</h2>
            </div>
            <div className="buy-benefits__grid">
              <div className="buy-benefit-card">
                <div className="buy-benefit-card__orb"></div>
                <span className="buy-benefit-card__number">01</span>
                <div className="buy-benefit-card__body">
                  <FaDollarSign className="buy-benefit-card__icon buy-benefit-card__icon--cyan" />
                  <div>
                    <h3>Clear Fee Design</h3>
                    <p>
                      Fee tiers are explicit by chain and pool, so users know
                      exactly what route they are using.
                    </p>
                  </div>
                </div>
              </div>

              <div className="buy-benefit-card">
                <div className="buy-benefit-card__orb"></div>
                <span className="buy-benefit-card__number">02</span>
                <div className="buy-benefit-card__body">
                  <FaChartBar className="buy-benefit-card__icon buy-benefit-card__icon--coral" />
                  <div>
                    <h3>Public Lock Visibility</h3>
                    <p>
                      Trust is built through live lock pages and verification
                      links, not only marketing claims.
                    </p>
                  </div>
                </div>
              </div>

              <div className="buy-benefit-card">
                <div className="buy-benefit-card__orb"></div>
                <span className="buy-benefit-card__number">03</span>
                <div className="buy-benefit-card__body">
                  <FaShieldAlt className="buy-benefit-card__icon buy-benefit-card__icon--green" />
                  <div>
                    <h3>Backed Pairing Strategy</h3>
                    <p>
                      Pair choices are designed to reinforce long-term liquidity
                      quality while keeping the Base route easy to verify.
                    </p>
                  </div>
                </div>
              </div>

              <div className="buy-benefit-card">
                <div className="buy-benefit-card__orb"></div>
                <span className="buy-benefit-card__number">04</span>
                <div className="buy-benefit-card__body">
                  <FaRocket className="buy-benefit-card__icon buy-benefit-card__icon--violet" />
                  <div>
                    <h3>Multi-Chain Expansion</h3>
                    <p>
                      REDDICKS keeps Radix roots while expanding visibility and
                      access on Base and later Solana.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section ref={roadmapRef} className={`buy-roadmap ${roadmapVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Current Liquidity Playbook</h2>
            <div className="buy-roadmap__card">
              <div className="buy-roadmap__card-orb"></div>
              <div className="buy-roadmap__intro">
                <p>
                  REDDICKS liquidity execution prioritizes trust, transparent
                  locks, and cross-chain access without abandoning Radix
                  utility.
                </p>
                <p>Current execution pillars:</p>
              </div>

              <div className="buy-roadmap__items">
                <div className="buy-roadmap__item">
                  <span className="buy-roadmap__item-number">01</span>
                  <div className="buy-roadmap__item-body">
                    <h4>Base Uniswap Positioning</h4>
                    <p>
                      Keep the live {BASE_PAIR_LABEL} pool visible with Team
                      Finance lock proof and public chart access.
                    </p>
                  </div>
                </div>

                <div className="buy-roadmap__item">
                  <span className="buy-roadmap__item-number">02</span>
                  <div className="buy-roadmap__item-body">
                    <h4>Public Lock Receipts</h4>
                    <p>
                      Keep Team Finance, DEXTools, and BaseScan links visible
                      across site sections as first-class trust anchors.
                    </p>
                  </div>
                </div>

                <div className="buy-roadmap__item">
                  <span className="buy-roadmap__item-number">03</span>
                  <div className="buy-roadmap__item-body">
                    <h4>Radix Repositioning at Unlock</h4>
                    <p>
                      Reposition Radix-side liquidity around C9 unlock timing
                      (around April 1, 2026) based on risk and fee structure.
                    </p>
                  </div>
                </div>

                <div className="buy-roadmap__item">
                  <span className="buy-roadmap__item-number">04</span>
                  <div className="buy-roadmap__item-body">
                    <h4>Fee-to-Treasury Loop</h4>
                    <p>
                      Treat liquidity fees as project funds for sustainable
                      long-term operations.
                    </p>
                  </div>
                </div>
              </div>

              <div className="buy-roadmap__cta">
                <p className="buy-roadmap__cta-text">
                  Start on the route you prefer now, then verify the live Base
                  lock before trading.
                </p>
                <a
                  href="#platforms-section"
                  onClick={scrollToPlatforms}
                  className="cta-button buy-cta buy-cta--primary"
                >
                  Buy REDDICKS <FaArrowRight />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Buy;

